import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import { parseEntityRef } from '@backstage/catalog-model';
import { useStarredEntities } from '@backstage/plugin-catalog-react';
import { HomeCard, HomeEmptyState } from './HomeCard';

// "Starred" — the entities the user pinned with the ☆ toggle, as chips.
export const StarredCard = () => {
  const { starredEntities } = useStarredEntities();

  const refs = useMemo(
    () =>
      [...starredEntities]
        .map(ref => parseEntityRef(ref))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [starredEntities],
  );

  return (
    <HomeCard title="Starred" eyebrow="Pinned by you">
      {refs.length === 0 ? (
        <HomeEmptyState
          icon={<StarBorderRoundedIcon sx={{ fontSize: 22, opacity: 0.6 }} />}
          title="No starred entities"
        >
          Star any component, template or doc (☆ in its header) to pin it here.
        </HomeEmptyState>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {refs.map(ref => (
            <Box
              key={`${ref.kind}:${ref.namespace}/${ref.name}`}
              component={RouterLink}
              to={`/catalog/${ref.namespace}/${ref.kind.toLowerCase()}/${
                ref.name
              }`}
              sx={theme => ({
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: 'background.default',
                borderRadius: '20px',
                px: 1.75,
                py: 0.9,
                textDecoration: 'none',
                color: 'text.primary',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.09),
                },
              })}
            >
              <StarRoundedIcon sx={{ fontSize: 15, color: '#eab308' }} />
              <Typography
                component="span"
                sx={{ fontSize: 12.5, fontWeight: 600 }}
              >
                {ref.name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </HomeCard>
  );
};
